// Insights.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import { FaChartLine, FaArrowLeft, FaEye, FaHeart, FaComment, FaShareAlt, FaBookmark, FaUsers, FaTimes } from "react-icons/fa";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import useAuthListener from "../../hooks/useAuthListener";
import { useLanguage } from "../../contexts/LanguageContext";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import "./Insights.css";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function Insights() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthListener();
  const { translations: tRaw } = useLanguage();
  const t = useMemo(() => tRaw?.insights || {}, [tRaw]);

  const [post, setPost] = useState(null);
  const [insights, setInsights] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Overlay state
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedInteractionType, setSelectedInteractionType] = useState(null);
  const [overlayUsers, setOverlayUsers] = useState([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState({});
  const [followingSet, setFollowingSet] = useState(new Set());
  
  // Accurate counts state (fetched from actual tables)
  const [accurateCounts, setAccurateCounts] = useState(null);

  const authUserId = useMemo(() => {
    return user?.uid || user?.id || user?.user_id || null;
  }, [user]);

  // Fetch post and insights data
  useEffect(() => {
    if (!postId || !authUserId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch post to verify ownership
        const { data: postData, error: postError } = await supabase
          .from('posts')
          .select('*')
          .eq('id', postId)
          .single();

        if (postError) throw postError;

        if (!postData) {
          setError('Post not found');
          setLoading(false);
          return;
        }

        // Verify ownership
        if (postData.user_id !== authUserId) {
          setError('You can only view insights for your own posts');
          setLoading(false);
          return;
        }

        setPost(postData);

        // Fetch user profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', authUserId)
          .single();

        if (profileError) {
          console.warn('[Insights] Error fetching profile:', profileError);
        } else if (profileData) {
          setUserProfile({
            username: profileData.username || profileData.display_name || `user_${authUserId.slice(0, 8)}`,
            profileImage: profileData.avatar_url || '/default-avatar.png'
          });
        }

        // Fetch insights
        const { data: insightsData, error: insightsError } = await supabase
          .from('post_insights')
          .select('*')
          .eq('post_id', postId)
          .order('created_at', { ascending: true });

        if (insightsError) throw insightsError;

        setInsights(insightsData || []);

        // Fetch accurate counts from actual tables - ALL COUNTS ARE UNIQUE USERS
        const viewerSet = new Set();
        
        // Get view insights (exclude post owner)
        (insightsData || []).forEach(row => {
          if (row.interaction_type === 'view' && row.viewer_id && row.viewer_id !== authUserId) {
            viewerSet.add(row.viewer_id);
          }
        });

        // Get unique users who liked (count unique users, not total likes)
        const { data: likesData } = await supabase
          .from('likes')
          .select('user_id')
          .eq('post_id', postId);
        const uniqueLikers = new Set((likesData || []).map(row => row.user_id).filter(Boolean));
        // Add likers to viewers (they saw the post if they liked it) - exclude post owner
        (likesData || []).forEach(row => {
          if (row.user_id && row.user_id !== authUserId) viewerSet.add(row.user_id);
        });

        // Get unique users who commented (count unique users, not total comments)
        const { data: commentsData } = await supabase
          .from('comments')
          .select('user_id')
          .eq('post_id', postId);
        const uniqueCommenters = new Set((commentsData || []).map(row => row.user_id).filter(Boolean));
        // Add commenters to viewers - exclude post owner
        (commentsData || []).forEach(row => {
          if (row.user_id && row.user_id !== authUserId) viewerSet.add(row.user_id);
        });

        // Get unique users who shared (count unique users, not total shares)
        const { data: sharesData } = await supabase
          .from('shares')
          .select('sharer_id')
          .eq('post_id', postId);
        const uniqueSharers = new Set((sharesData || []).map(row => row.sharer_id).filter(Boolean));
        // Add sharers to viewers - exclude post owner
        (sharesData || []).forEach(row => {
          if (row.sharer_id && row.sharer_id !== authUserId) viewerSet.add(row.sharer_id);
        });

        // Get unique users who saved (count unique users, not total saves)
        const { data: savesData } = await supabase
          .from('saves')
          .select('user_id')
          .eq('post_id', postId);
        const uniqueSavers = new Set((savesData || []).map(row => row.user_id).filter(Boolean));
        // Add savers to viewers - exclude post owner
        (savesData || []).forEach(row => {
          if (row.user_id && row.user_id !== authUserId) viewerSet.add(row.user_id);
        });

        // Get unique followers who interacted with the post
        const followerInteractors = new Set();
        const { data: followersData } = await supabase
          .from('follows')
          .select('follower_id')
          .eq('following_id', authUserId);
        const followerIds = new Set((followersData || []).map(f => f.follower_id));
        
        // Check which followers are in the viewers set
        viewerSet.forEach(viewerId => {
          if (followerIds.has(viewerId)) {
            followerInteractors.add(viewerId);
          }
        });

        setAccurateCounts({
          views: viewerSet.size, // Unique viewers excluding post owner
          likes: uniqueLikers.size, // Unique users who liked
          comments: uniqueCommenters.size, // Unique users who commented
          shares: uniqueSharers.size, // Unique users who shared
          saves: uniqueSavers.size, // Unique users who saved
          followers: followerInteractors.size // Unique followers who interacted
        });

      } catch (err) {
        console.error('[Insights] Error fetching data:', err);
        setError(err?.message || 'Failed to load insights');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [postId, authUserId]);

  // Fetch current user's following list
  useEffect(() => {
    if (!authUserId) return;

    const fetchFollowing = async () => {
      try {
        const { data, error } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', authUserId);

        if (error) {
          console.warn('[Insights] Error fetching following:', error);
          return;
        }

        const followingIds = new Set((data || []).map(f => f.following_id));
        setFollowingSet(followingIds);
      } catch (err) {
        console.error('[Insights] Error fetching following:', err);
      }
    };

    fetchFollowing();
  }, [authUserId]);

  // Helper to format timestamp (relative)
  const formatTimestamp = useCallback((dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.justNow || 'Just now';
    if (diffMins < 60) return `${diffMins}${t.minAgo || 'm ago'}`;
    if (diffHours < 24) return `${diffHours}${t.hourAgo || 'h ago'}`;
    if (diffDays < 7) return `${diffDays}${t.dayAgo || 'd ago'}`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  }, [t]);

  // Helper to format full timestamp (date + time)
  const formatFullTimestamp = useCallback((dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  // State for showing full timestamp
  const [expandedTimestamps, setExpandedTimestamps] = useState(new Set());

  const toggleTimestamp = useCallback((userId) => {
    setExpandedTimestamps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  }, []);

  // Fetch users by interaction type
  const fetchUsersByInteractionType = useCallback(async (interactionType) => {
    if (!postId || !authUserId) return;

    setOverlayLoading(true);
    setOverlayUsers([]);

    try {
      // Map: user_id -> { timestamp, ... }
      const userTimestampMap = new Map();

      switch (interactionType) {
        case 'likes':
          {
            const { data, error } = await supabase
              .from('likes')
              .select('user_id, created_at')
              .eq('post_id', postId)
              .order('created_at', { ascending: false });
            if (error) throw error;
            (data || []).forEach(row => {
              if (row.user_id && !userTimestampMap.has(row.user_id)) {
                userTimestampMap.set(row.user_id, row.created_at);
              }
            });
          }
          break;
        case 'comments':
          {
            const { data, error } = await supabase
              .from('comments')
              .select('user_id, created_at')
              .eq('post_id', postId)
              .order('created_at', { ascending: false });
            if (error) throw error;
            (data || []).forEach(row => {
              if (row.user_id && !userTimestampMap.has(row.user_id)) {
                userTimestampMap.set(row.user_id, row.created_at);
              }
            });
          }
          break;
        case 'shares':
          {
            const { data, error } = await supabase
              .from('shares')
              .select('sharer_id, created_at')
              .eq('post_id', postId)
              .order('created_at', { ascending: false });
            if (error) throw error;
            (data || []).forEach(row => {
              if (row.sharer_id && !userTimestampMap.has(row.sharer_id)) {
                userTimestampMap.set(row.sharer_id, row.created_at);
              }
            });
          }
          break;
        case 'saves':
          {
            const { data, error } = await supabase
              .from('saves')
              .select('user_id, created_at')
              .eq('post_id', postId)
              .order('created_at', { ascending: false });
            if (error) throw error;
            (data || []).forEach(row => {
              if (row.user_id && !userTimestampMap.has(row.user_id)) {
                userTimestampMap.set(row.user_id, row.created_at);
              }
            });
          }
          break;
        case 'views':
          {
            // For views, we collect from post_insights (view) + all interactions
            // EXCLUDE the post owner - views are OTHER accounts who saw the post
            const { data, error } = await supabase
              .from('post_insights')
              .select('viewer_id, created_at')
              .eq('post_id', postId)
              .eq('interaction_type', 'view');
            if (error) throw error;
            (data || []).forEach(row => {
              // Exclude post owner from views
              if (row?.viewer_id && row.viewer_id !== authUserId) {
                const existing = userTimestampMap.get(row.viewer_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.viewer_id, row.created_at);
                }
              }
            });

            // Also add users from likes, comments, shares, saves (they viewed if they interacted)
            // Exclude post owner
            const { data: likeRows, error: likesError } = await supabase
              .from('likes')
              .select('user_id, created_at')
              .eq('post_id', postId);
            if (likesError) throw likesError;
            (likeRows || []).forEach(row => {
              if (row.user_id && row.user_id !== authUserId) {
                const existing = userTimestampMap.get(row.user_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.user_id, row.created_at);
                }
              }
            });

            const { data: commentRows, error: commentsError } = await supabase
              .from('comments')
              .select('user_id, created_at')
              .eq('post_id', postId);
            if (commentsError) throw commentsError;
            (commentRows || []).forEach(row => {
              if (row.user_id && row.user_id !== authUserId) {
                const existing = userTimestampMap.get(row.user_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.user_id, row.created_at);
                }
              }
            });

            const { data: shareRows, error: sharesError } = await supabase
              .from('shares')
              .select('sharer_id, created_at')
              .eq('post_id', postId);
            if (sharesError) throw sharesError;
            (shareRows || []).forEach(row => {
              if (row.sharer_id && row.sharer_id !== authUserId) {
                const existing = userTimestampMap.get(row.sharer_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.sharer_id, row.created_at);
                }
              }
            });

            const { data: saveRows, error: savesError } = await supabase
              .from('saves')
              .select('user_id, created_at')
              .eq('post_id', postId);
            if (savesError) throw savesError;
            (saveRows || []).forEach(row => {
              if (row.user_id && row.user_id !== authUserId) {
                const existing = userTimestampMap.get(row.user_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.user_id, row.created_at);
                }
              }
            });
            // DO NOT include post owner in views - views are other accounts only
          }
          break;
        case 'followers':
          {
            // Get all followers of the post owner
            const { data: followersData, error: followersError } = await supabase
              .from('follows')
              .select('follower_id')
              .eq('following_id', authUserId);
            if (followersError) throw followersError;
            const followerIds = new Set((followersData || []).map(f => f.follower_id));

            // Now find which followers interacted with the post (viewed, liked, commented, shared, saved)
            // Check post_insights for views
            const { data: viewData, error: viewError } = await supabase
              .from('post_insights')
              .select('viewer_id, created_at')
              .eq('post_id', postId)
              .eq('interaction_type', 'view');
            if (viewError) throw viewError;
            (viewData || []).forEach(row => {
              if (row.viewer_id && followerIds.has(row.viewer_id)) {
                const existing = userTimestampMap.get(row.viewer_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.viewer_id, row.created_at);
                }
              }
            });

            // Check likes
            const { data: likeRows, error: likesError } = await supabase
              .from('likes')
              .select('user_id, created_at')
              .eq('post_id', postId);
            if (likesError) throw likesError;
            (likeRows || []).forEach(row => {
              if (row.user_id && followerIds.has(row.user_id)) {
                const existing = userTimestampMap.get(row.user_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.user_id, row.created_at);
                }
              }
            });

            // Check comments
            const { data: commentRows, error: commentsError } = await supabase
              .from('comments')
              .select('user_id, created_at')
              .eq('post_id', postId);
            if (commentsError) throw commentsError;
            (commentRows || []).forEach(row => {
              if (row.user_id && followerIds.has(row.user_id)) {
                const existing = userTimestampMap.get(row.user_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.user_id, row.created_at);
                }
              }
            });

            // Check shares
            const { data: shareRows, error: sharesError } = await supabase
              .from('shares')
              .select('sharer_id, created_at')
              .eq('post_id', postId);
            if (sharesError) throw sharesError;
            (shareRows || []).forEach(row => {
              if (row.sharer_id && followerIds.has(row.sharer_id)) {
                const existing = userTimestampMap.get(row.sharer_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.sharer_id, row.created_at);
                }
              }
            });

            // Check saves
            const { data: saveRows, error: savesError } = await supabase
              .from('saves')
              .select('user_id, created_at')
              .eq('post_id', postId);
            if (savesError) throw savesError;
            (saveRows || []).forEach(row => {
              if (row.user_id && followerIds.has(row.user_id)) {
                const existing = userTimestampMap.get(row.user_id);
                if (!existing || new Date(row.created_at) < new Date(existing)) {
                  userTimestampMap.set(row.user_id, row.created_at);
                }
              }
            });
          }
          break;
        default:
          return;
      }

      const userIds = [...userTimestampMap.keys()];

      if (userIds.length === 0) {
        setOverlayUsers([]);
        setOverlayLoading(false);
        return;
      }

      // Fetch profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, username, avatar_url')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Map profiles with avatar URLs and timestamps
      const users = (profilesData || []).map(profile => {
        let avatarUrl = '/default-avatar.png';
        if (profile.avatar_url) {
          try {
            const path = `${profile.user_id}/${profile.avatar_url}`;
            const { data } = supabase.storage.from('avatars').getPublicUrl(path);
            avatarUrl = data?.publicUrl || avatarUrl;
          } catch {
            avatarUrl = '/default-avatar.png';
          }
        }

        return {
          id: profile.user_id,
          username: profile.display_name || profile.username || `user_${profile.user_id.slice(0, 8)}`,
          profileImage: avatarUrl,
          isFollowed: followingSet.has(profile.user_id),
          timestamp: userTimestampMap.get(profile.user_id) || null
        };
      });

      // Sort by timestamp (most recent first)
      users.sort((a, b) => {
        if (!a.timestamp) return 1;
        if (!b.timestamp) return -1;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });

      setOverlayUsers(users);
    } catch (err) {
      console.error('[Insights] Error fetching users:', err);
      setOverlayUsers([]);
    } finally {
      setOverlayLoading(false);
    }
  }, [postId, authUserId, followingSet]);

  // Handle stat card click
  const handleStatCardClick = useCallback((interactionType) => {
    setSelectedInteractionType(interactionType);
    setIsOverlayOpen(true);
    fetchUsersByInteractionType(interactionType);
  }, [fetchUsersByInteractionType]);

  // Toggle follow/unfollow
  const toggleFollow = useCallback(async (targetId, isCurrentlyFollowed) => {
    if (!authUserId) return;

    setFollowLoading(prev => ({ ...prev, [targetId]: true }));

    try {
      if (isCurrentlyFollowed) {
        // Unfollow
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', authUserId)
          .eq('following_id', targetId);

        if (error) throw error;
      } else {
        // Follow
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: authUserId,
            following_id: targetId
          });

        if (error) throw error;
      }

      // Update local state
      setFollowingSet(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFollowed) {
          newSet.delete(targetId);
        } else {
          newSet.add(targetId);
        }
        return newSet;
      });

      setOverlayUsers(prev =>
        prev.map(u =>
          u.id === targetId ? { ...u, isFollowed: !isCurrentlyFollowed } : u
        )
      );
    } catch (err) {
      console.error('[Insights] Error toggling follow:', err);
    } finally {
      setFollowLoading(prev => ({ ...prev, [targetId]: false }));
    }
  }, [authUserId]);

  // Process insights data for charts
  const chartData = useMemo(() => {
    if (!insights.length) return null;

    // Group by date
    const dateMap = new Map();
    insights.forEach(insight => {
      const insightDate = new Date(insight.created_at);
      const dateKey = insightDate.toISOString().split('T')[0]; // YYYY-MM-DD for grouping
      const dateLabel = insightDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateLabel,
          dateKey: dateKey,
          timestamp: insightDate.getTime(),
          views: 0,
          likes: 0,
          comments: 0,
          shares: 0,
          saves: 0
        });
      }
      const entry = dateMap.get(dateKey);
      if (insight.interaction_type === 'view') entry.views++;
      else if (insight.interaction_type === 'like') entry.likes++;
      else if (insight.interaction_type === 'comment') entry.comments++;
      else if (insight.interaction_type === 'share') entry.shares++;
      else if (insight.interaction_type === 'save') entry.saves++;
    });

    // Sort by timestamp
    return Array.from(dateMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [insights]);

  // Calculate summary statistics
  const stats = useMemo(() => {
    if (!insights.length) return null;

    const views = insights.filter(i => i.interaction_type === 'view').length;
    const likes = insights.filter(i => i.interaction_type === 'like').length;
    const comments = insights.filter(i => i.interaction_type === 'comment').length;
    const shares = insights.filter(i => i.interaction_type === 'share').length;
    const saves = insights.filter(i => i.interaction_type === 'save').length;
    const followers = insights.filter(i => i.is_follower === true).length;
    const nonFollowers = insights.filter(i => i.is_follower === false).length;

    const uniqueInteractors = new Set(
      insights
        .filter(i => i.viewer_id)
        .map(i => i.viewer_id)
    );
    const adjustedViews = Math.max(views, uniqueInteractors.size);

    return {
      views: adjustedViews,
      likes,
      comments,
      shares,
      saves,
      followers,
      nonFollowers,
      total: insights.length
    };
  }, [insights]);

  // Interaction type distribution for pie chart
  const interactionDistribution = useMemo(() => {
    if (!stats) return null;

    return [
      { name: t.views || 'Views', value: stats.views },
      { name: t.likes || 'Likes', value: stats.likes },
      { name: t.comments || 'Comments', value: stats.comments },
      { name: t.shares || 'Shares', value: stats.shares },
      { name: t.saves || 'Saves', value: stats.saves }
    ].filter(item => item.value > 0);
  }, [stats, t]);

  if (loading) {
    return (
      <div className="insights-page">
        <div className="insights-loading">{t.loading || "Loading insights…"}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-page">
        <button className="insights-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft />
        </button>
        <div className="insights-error">
          <p>{error}</p>
          <button onClick={() => navigate(-1)}>{t.goBack || "Go Back"}</button>
        </div>
      </div>
    );
  }

  const hasInsights = insights.length > 0;

  return (
    <div className="insights-page">
      <button className="insights-back-btn" onClick={() => navigate(-1)} title={t.goBack || "Go back"}>
        <FaArrowLeft />
      </button>

      <FloatingSidebarButton userProfile={userProfile || { name: user?.username, profileImage: user?.avatar_url }} />

      <div className="insights-container">
        <div className="insights-header">
          <h1 className="insights-title">{t.title || "Post Insights"}</h1>
          {post?.caption && (
            <p className="insights-post-caption">{post.caption}</p>
          )}
        </div>

        {!hasInsights ? (
          <div className="insights-empty-state">
            <div className="insights-empty-icon">
              <FaChartLine />
            </div>
            <h2 className="insights-empty-title">{t.noInsightsTitle || "No insights available yet"}</h2>
            <p className="insights-empty-subtitle">
              {t.noInsightsSubtitle || "Insights will appear here once your posts gets engagements"}
            </p>
          </div>
        ) : (
          <div className="insights-content">
            {/* Summary Stats */}
            <div className="insights-stats-grid">
              <div className="insight-stat-card" onClick={() => handleStatCardClick('views')}>
                <div className="insight-stat-icon views">
                  <FaEye />
                </div>
                <div className="insight-stat-info">
                  <span className="insight-stat-value">{accurateCounts?.views ?? stats.views}</span>
                  <span className="insight-stat-label">{t.views || "Views"}</span>
                </div>
              </div>

              <div className="insight-stat-card" onClick={() => handleStatCardClick('likes')}>
                <div className="insight-stat-icon likes">
                  <FaHeart />
                </div>
                <div className="insight-stat-info">
                  <span className="insight-stat-value">{accurateCounts?.likes ?? stats.likes}</span>
                  <span className="insight-stat-label">{t.likes || "Likes"}</span>
                </div>
              </div>

              <div className="insight-stat-card" onClick={() => handleStatCardClick('comments')}>
                <div className="insight-stat-icon comments">
                  <FaComment />
                </div>
                <div className="insight-stat-info">
                  <span className="insight-stat-value">{accurateCounts?.comments ?? stats.comments}</span>
                  <span className="insight-stat-label">{t.comments || "Comments"}</span>
                </div>
              </div>

              <div className="insight-stat-card" onClick={() => handleStatCardClick('shares')}>
                <div className="insight-stat-icon shares">
                  <FaShareAlt />
                </div>
                <div className="insight-stat-info">
                  <span className="insight-stat-value">{accurateCounts?.shares ?? stats.shares}</span>
                  <span className="insight-stat-label">{t.shares || "Shares"}</span>
                </div>
              </div>

              <div className="insight-stat-card" onClick={() => handleStatCardClick('saves')}>
                <div className="insight-stat-icon saves">
                  <FaBookmark />
                </div>
                <div className="insight-stat-info">
                  <span className="insight-stat-value">{accurateCounts?.saves ?? stats.saves}</span>
                  <span className="insight-stat-label">{t.saves || "Saves"}</span>
                </div>
              </div>

              <div className="insight-stat-card" onClick={() => handleStatCardClick('followers')}>
                <div className="insight-stat-icon followers">
                  <FaUsers />
                </div>
                <div className="insight-stat-info">
                  <span className="insight-stat-value">{accurateCounts?.followers ?? stats.followers}</span>
                  <span className="insight-stat-label">{t.followers || "Followers"}</span>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="insights-charts">
              {/* Engagement Over Time */}
              {chartData && chartData.length > 0 && (
                <div className="insight-chart-card">
                  <h3 className="insight-chart-title">{t.engagementOverTime || "Engagement Over Time"}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="views" stroke="#0088FE" strokeWidth={2} name={t.views || "Views"} />
                      <Line type="monotone" dataKey="likes" stroke="#00C49F" strokeWidth={2} name={t.likes || "Likes"} />
                      <Line type="monotone" dataKey="comments" stroke="#FFBB28" strokeWidth={2} name={t.comments || "Comments"} />
                      <Line type="monotone" dataKey="shares" stroke="#FF8042" strokeWidth={2} name={t.shares || "Shares"} />
                      <Line type="monotone" dataKey="saves" stroke="#8884d8" strokeWidth={2} name={t.saves || "Saves"} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Interaction Distribution */}
              {interactionDistribution && interactionDistribution.length > 0 && (
                <div className="insight-chart-card">
                  <h3 className="insight-chart-title">{t.interactionDistribution || "Interaction Distribution"}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={interactionDistribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {interactionDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Follower vs Non-Follower */}
              {stats && (stats.followers > 0 || stats.nonFollowers > 0) && (
                <div className="insight-chart-card">
                  <h3 className="insight-chart-title">{t.audienceBreakdown || "Audience Breakdown"}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={[
                      { name: t.followers || "Followers", value: stats.followers },
                      { name: t.nonFollowers || "Non-Followers", value: stats.nonFollowers }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Details Overlay */}
      {isOverlayOpen && (
        <div className="insight-card-details-overlay" onClick={() => setIsOverlayOpen(false)}>
          <div className="insight-card-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="insight-card-details-header">
              <h2 className="insight-card-details-title">
                {selectedInteractionType === 'likes' && (t.likes || 'Likes')}
                {selectedInteractionType === 'comments' && (t.comments || 'Comments')}
                {selectedInteractionType === 'shares' && (t.shares || 'Shares')}
                {selectedInteractionType === 'saves' && (t.saves || 'Saves')}
                {selectedInteractionType === 'views' && (t.views || 'Views')}
                {selectedInteractionType === 'followers' && (t.followers || 'Followers')}
              </h2>
              <button className="insight-card-details-close" onClick={() => setIsOverlayOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="insight-card-details-content">
              {overlayLoading ? (
                <div className="insight-card-details-loading">{t.loading || "Loading..."}</div>
              ) : overlayUsers.length === 0 ? (
                <div className="insight-card-details-empty">
                  {t.noUsers || "No users found"}
                </div>
              ) : (
                <div className="insight-card-details-list">
                  {overlayUsers.map((user) => (
                    <div key={user.id} className="insight-card-details-item">
                      <Link to={`/profile/${user.id}`} className="insight-card-details-user-info">
                        <img
                          src={user.profileImage || "/default-avatar.png"}
                          alt={user.username}
                          className="insight-card-details-avatar"
                        />
                        <div className="insight-card-details-user-text">
                          <span className="insight-card-details-username">
                            {user.username}
                          </span>
                          {user.timestamp && (
                            <span 
                              className="insight-card-details-timestamp"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleTimestamp(user.id);
                              }}
                              title={t.clickForDetails || "Click for full date/time"}
                            >
                              {expandedTimestamps.has(user.id) 
                                ? formatFullTimestamp(user.timestamp) 
                                : formatTimestamp(user.timestamp)}
                            </span>
                          )}
                        </div>
                      </Link>
                      {user.id !== authUserId && (
                        <button
                          className={`insight-card-details-follow-btn ${user.isFollowed ? "unfollow" : ""}`}
                          onClick={() => toggleFollow(user.id, user.isFollowed)}
                          disabled={followLoading[user.id]}
                        >
                          {followLoading[user.id]
                            ? "..."
                            : user.isFollowed
                            ? (t.unfollow || "Unfollow")
                            : (t.follow || "Follow")}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

